// frontend/src/components/admin/DeliveryStaffDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    User,
    Mail,
    Calendar,
    MapPin,
    Package,
    Phone,
    ArrowLeft,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    Clock
} from 'lucide-react';
import axios from 'axios';
import { getFormattedImageUrl } from '../../utils/ImageUtil';

const DeliveryStaffDetail = () => {
    const [staff, setStaff] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();

    const servingAreas = {
        '562130': 'Gollarahatti',
        '562130': 'Kadabagere',
        '560112': 'Chikka Gollarahatti',
        '560091': 'Machohalli',
        '560091': 'Anjana Nagar',
        '560091': 'Byadarahalli',
        '562130': 'Tavarekere',
        '560091': 'Herohalli',
        '560091': 'Viswaneedam',
        '560091': 'Sunkadakatte',
        '560091': 'Muddayanapalya',
        '560091': 'Annapurneshwari Nagar',
        '560091': 'Kottigepalya',
        '560091': 'Sumanahalli'
    };

    const fetchStaffDetails = useCallback(async () => {
        try {
            const { data } = await axios.get(`/api/v1/admin/delivery-staff/${id}`, {
                withCredentials: true
            });
            if (data.success) {
                setStaff(data.deliveryStaff);
                setError(null);
                console.log('Staff data loaded:', data.deliveryStaff);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Error fetching staff details';
            setError(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
            toast.error(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
            if (error.response?.status === 404) {
                navigate('/delivery-staff-list');
            }
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchStaffDetails();
    }, [fetchStaffDetails]);

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center min-h-screen">
                    <Loader2 className="w-8 h-8 animate-spin text-[#dc2626]" />
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen px-3">
                    <AlertTriangle className="w-12 h-12 text-[#dc2626] mb-3" />
                    <h2 className="text-lg font-bold text-gray-900 mb-1.5">Error Loading Staff Details</h2>
                    <p className="text-sm text-gray-500 mb-3">
                        {typeof error === 'object' ? error.message || 'An error occurred' : error}
                    </p>
                    <button
                        onClick={() => navigate('/delivery-staff-list')}
                        className="inline-flex items-center px-3 py-1.5 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors text-sm"
                    >
                        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                        Back to Staff List
                    </button>
                </div>
            );
        }

        if (!staff) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen px-3">
                    <AlertTriangle className="w-12 h-12 text-[#dc2626] mb-3" />
                    <h2 className="text-lg font-bold text-gray-900 mb-1.5">Staff Not Found</h2>
                    <p className="text-sm text-gray-500 mb-3">The delivery staff member you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/delivery-staff-list')}
                        className="inline-flex items-center px-3 py-1.5 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors text-sm"
                    >
                        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                        Back to Staff List
                    </button>
                </div>
            );
        }

        // Format and ensure profile picture URL is correct
        const profilePicture = staff.profilePicture ? getFormattedImageUrl(staff.profilePicture) : null;
        console.log('Profile picture URL:', profilePicture);

        return (
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
                <div className="mb-4">
                    <button
                        onClick={() => navigate('/delivery-staff-list')}
                        className="inline-flex items-center text-[#dc2626] hover:text-[#dc2626]/80 transition-colors text-sm"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                        Back to Staff List
                    </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {/* Header Section */}
                    <div className="bg-[#dc2626] px-4 py-6">
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center shadow-lg overflow-hidden">
                                {profilePicture ? (
                                    <img 
                                        src={profilePicture} 
                                        alt={staff.name} 
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                            console.error('Image failed to load:', e);
                                            e.target.onerror = null;
                                            e.target.src = '';
                                            e.target.parentElement.innerHTML = '<div class="h-full w-full flex items-center justify-center"><User class="h-10 w-10 text-[#dc2626]" /></div>';
                                        }}
                                    />
                                ) : (
                                    <User className="h-10 w-10 text-[#dc2626]" />
                                )}
                            </div>
                            <div className="text-center sm:text-left">
                                <h1 className="text-xl font-bold text-white font-['Crimson_Text']">{staff.name}</h1>
                                <div className="mt-1.5 flex items-center justify-center sm:justify-start gap-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center ${
                                        staff.isBlocked
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {staff.isBlocked ? (
                                            <AlertTriangle className="w-3 h-3 mr-0.5" />
                                        ) : (
                                            <CheckCircle2 className="w-3 h-3 mr-0.5" />
                                        )}
                                        {staff.isBlocked ? 'Blocked' : 'Active'}
                                    </span>
                                    <span className="bg-white/20 text-white px-2 py-0.5 rounded-full text-xs">
                                        ID: {staff.loginId}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {/* Personal Information */}
                            <div className="space-y-4">
                                <h2 className="text-lg font-bold text-gray-900 font-['Crimson_Text']">
                                    Personal Information
                                </h2>
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <div className="flex items-center">
                                        <Phone className="w-4 h-4 text-[#dc2626] mr-3" />
                                        <div>
                                            <p className="text-xs text-gray-500">Contact Number</p>
                                            <p className="font-medium text-sm text-gray-900">
                                                {staff.phone || 'Not provided'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <Mail className="w-4 h-4 text-[#dc2626] mr-3" />
                                        <div>
                                            <p className="text-xs text-gray-500">Email Address</p>
                                            <p className="font-medium text-sm text-gray-900">
                                                {staff.email || 'Not provided'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <Calendar className="w-4 h-4 text-[#dc2626] mr-3" />
                                        <div>
                                            <p className="text-xs text-gray-500">Age</p>
                                            <p className="font-medium text-sm text-gray-900">{staff.age} Years</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <MapPin className="w-4 h-4 text-[#dc2626] mr-3 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Address</p>
                                            <p className="font-medium text-sm text-gray-900">{staff.address}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <Clock className="w-4 h-4 text-[#dc2626] mr-3" />
                                        <div>
                                            <p className="text-xs text-gray-500">Account Created</p>
                                            <p className="font-medium text-sm text-gray-900">
                                                {new Date(staff.createdAt).toLocaleDateString('en-IN', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Delivery Areas */}
                            <div className="space-y-4">
                                <h2 className="text-lg font-bold text-gray-900 font-['Crimson_Text']">
                                    Assigned Delivery Areas
                                </h2>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="grid gap-3">
                                        {staff.assignedPincodes.map(pincode => (
                                            <div
                                                key={pincode}
                                                className="flex items-center bg-white p-3 rounded-lg border border-gray-100"
                                            >
                                                <Package className="w-4 h-4 text-[#dc2626] mr-3" />
                                                <div>
                                                    <p className="font-medium text-sm text-gray-900">
                                                        {servingAreas[pincode]}
                                                    </p>
                                                    <p className="text-xs text-gray-500">PIN: {pincode}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {staff.assignedPincodes.length === 0 && (
                                        <div className="text-center py-6">
                                            <Package className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">No delivery areas assigned yet</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return renderContent();
};

export default DeliveryStaffDetail;